/**
 * Empresa ativa de quem pertence a mais de uma (organizations do Keycloak).
 *
 * A API decide o tenant sozinha quando o usuário tem uma empresa; com mais de
 * uma, exige o cabeçalho `X-Tenant` e responde `400 tenant_selection_required`
 * com a lista (resolve-active-tenant.use-case). Em 2026-09-28 o SPA não mandava
 * o cabeçalho e não tinha onde escolher: quem estava nas duas empresas não
 * conseguia usar o ERP.
 *
 * A escolha fica no navegador (é preferência de quem opera, não dado), e a
 * lista conhecida vem do próprio erro da API — não há rota que liste empresas
 * antes de haver uma escolhida. Todo acesso ao storage é protegido: aba
 * privada ou storage bloqueado não pode travar o login.
 */
const SELECTED_KEY = 'erp.tenant';
const KNOWN_KEY = 'erp.tenants';

export interface TenantOption {
  id: number;
  slug: string;
}

export function getSelectedTenant(): string | null {
  try {
    return localStorage.getItem(SELECTED_KEY);
  } catch {
    return null;
  }
}

export function setSelectedTenant(slug: string): void {
  try {
    localStorage.setItem(SELECTED_KEY, slug);
  } catch {
    /* sem storage: a escolha vale até recarregar */
  }
}

export function clearSelectedTenant(): void {
  try {
    localStorage.removeItem(SELECTED_KEY);
  } catch {
    /* nada a limpar */
  }
}

export function getKnownTenants(): TenantOption[] {
  try {
    const raw = localStorage.getItem(KNOWN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (t): t is TenantOption =>
            typeof t === 'object' && t !== null && typeof t.slug === 'string' && typeof t.id === 'number'
        )
      : [];
  } catch {
    return [];
  }
}

export function setKnownTenants(list: TenantOption[]): void {
  try {
    localStorage.setItem(KNOWN_KEY, JSON.stringify(list));
  } catch {
    /* sem storage: o seletor aparece de novo no próximo carregamento */
  }
}

/** Lista das empresas vinda do `details` do 400 tenant_selection_required. */
export function tenantsFromErrorDetails(details: unknown): TenantOption[] {
  const list = (details as { tenants?: unknown } | null | undefined)?.tenants;
  if (!Array.isArray(list)) return [];
  return list.filter(
    (t): t is TenantOption =>
      typeof t === 'object' && t !== null && typeof t.slug === 'string' && typeof t.id === 'number'
  );
}

/** Rótulo legível do slug ("pooltrack" → "Pooltrack"). */
export function tenantLabel(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}
